import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import ZahlungsplanHeader from "./zahlungsplan/ZahlungsplanHeader";
import ZahlungsplanTable from "./zahlungsplan/ZahlungsplanTable";
import ZahlungsplanContextMenu from "./zahlungsplan/ZahlungsplanContextMenu";
import ZahlungsplanDetailPopover from "./zahlungsplan/ZahlungsplanDetailPopover";
import ZahlungsplanBelegDialog from "./zahlungsplan/ZahlungsplanBelegDialog";
import ZahlungsplanBelegeListe from "./zahlungsplan/ZahlungsplanBelegeListe";
import {
  getInvoiceDateForWeek,
  getEffectiveWeekForDate,
  formatDateISO,
  generateInvoiceReference,
} from "./zahlungsplan/zahlungsplanUtils";
import {
  saveBudgetEntry,
  deleteBudgetEntry,
  updateBudgetEntryStatus,
} from "../services/azureBudgetService";
import { getAllPartners, createPartner } from "../services/azurePartnersService";
import { getProjectPartners, addProjectPartner } from "../services/azureRelationsService";
import { calculateMwstAmounts } from "../services/mwstService";
import { exportZahlungsplanToExcel } from "./zahlungsplan/zahlungsplanExport";
import { getZahlungsplanSettings, saveZahlungsplanSettings } from "../services/azureProjectsService";
import { getBelege, uploadBeleg, deleteBeleg } from "../services/azureBelegeService";
import { useAuth } from "../hooks/useAuth";

const ZahlungsplanModal = ({
  project,
  projectCode,
  phases,
  allBudgetEntries,
  onClose,
  onBudgetChange,
  readOnly = false,
}) => {
  // Global settings
  const [globalSettings, setGlobalSettings] = useState({
    mwstSatz: 8.1,
    nebenkostenSatz: 0,
    zahlungsfrist: 30,
    rechnungstag: 5, // Friday
    rechnungsnummerPrefix: projectCode || "",
  });

  // UI state
  const [contextMenu, setContextMenu] = useState(null);
  const [detailPopover, setDetailPopover] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expandedPhases, setExpandedPhases] = useState(new Set());
  const [hideEmptyWeeks, setHideEmptyWeeks] = useState(false);
  const [selectedPlaner, setSelectedPlaner] = useState(null); // null = all, or { id, name }
  const [incomeBudgets, setIncomeBudgets] = useState({}); // { phaseKey: number } overrides for phase budgets
  const [isFullscreen, setIsFullscreen] = useState(false);
  const settingsLoaded = useRef(false);

  // Belege (PDF-Rechnungsbelege, projectBelege-Tabelle)
  const { user } = useAuth();
  const [belege, setBelege] = useState([]);
  const [belegDialog, setBelegDialog] = useState(null); // { entry } | null
  const [showBelegeListe, setShowBelegeListe] = useState(false);

  useEffect(() => {
    if (!projectCode) return;
    getBelege(projectCode)
      .then(setBelege)
      .catch((err) => console.error("Error loading Belege:", err));
  }, [projectCode]);

  // Escape beendet nur den Vollbildmodus (nicht das Modal)
  useEffect(() => {
    if (!isFullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setIsFullscreen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isFullscreen]);

  // Load Zahlungsplan settings from Azure on mount
  useEffect(() => {
    if (!project?.id) return;
    const load = async () => {
      const saved = await getZahlungsplanSettings(project.id);
      if (saved) {
        settingsLoaded.current = false; // prevent save trigger while applying
        // Mit Defaults mergen: Settings, die vor neueren Feldern (z.B.
        // nebenkostenSatz) gespeichert wurden, dürfen diese nicht auf
        // undefined setzen.
        if (saved.globalSettings) {
          setGlobalSettings((prev) => ({ ...prev, ...saved.globalSettings }));
        }
        if (saved.incomeBudgets) setIncomeBudgets(saved.incomeBudgets);
        // Allow save triggers after a tick
        setTimeout(() => { settingsLoaded.current = true; }, 0);
      } else {
        settingsLoaded.current = true;
      }
    };
    load();
  }, [project?.id]);

  // Auto-save settings to Azure (debounced)
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!project?.id || !settingsLoaded.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveZahlungsplanSettings(project.id, {
        globalSettings,
        incomeBudgets,
      }).catch((err) => console.error("Error saving Zahlungsplan settings:", err));
    }, 800);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [globalSettings, incomeBudgets, project?.id]);

  // Partner management state
  const [allPartners, setAllPartners] = useState([]);
  const [projectPartnersList, setProjectPartnersList] = useState([]);
  const [addPartnerForPhase, setAddPartnerForPhase] = useState(null); // phaseKey if partner search is open
  const [partnerSearchValue, setPartnerSearchValue] = useState("");
  const [showPartnerRoleModal, setShowPartnerRoleModal] = useState(false);
  const [pendingPartner, setPendingPartner] = useState(null);
  // Manually added partner columns (phaseKey → [{partnerId, partnerName}])
  const [manualPartnerColumns, setManualPartnerColumns] = useState({});

  // Load partners on mount
  React.useEffect(() => {
    const loadPartners = async () => {
      try {
        const [partners, projectRelations] = await Promise.all([
          getAllPartners(),
          getProjectPartners(projectCode),
        ]);
        setAllPartners(partners);
        const linked = projectRelations
          .filter((r) => r.targetId)
          .map((r) => {
            const p = partners.find((p) => String(p.id) === String(r.targetId));
            return {
              id: r.targetId,
              name: p?.name || `Partner ${r.targetId}`,
              role: r.relationType || r.function || "Partner",
            };
          });
        setProjectPartnersList(linked);
      } catch (err) {
        console.error("Error loading partners:", err);
      }
    };
    loadPartners();
  }, [projectCode]);

  // Filter budget entries for this project (single-occurrence only for grid)
  const projectEntries = useMemo(() => {
    return allBudgetEntries.filter((e) => {
      if (e.typ_timing === "Regelmässig") return false;
      if (e.typ !== "Einnahme" && e.typ !== "Ausgabe") return false;
      return true;
    });
  }, [allBudgetEntries]);

  // Sort phases by startDate (earliest first, year then month then day)
  const sortedPhases = useMemo(() => {
    return [...phases].sort((a, b) => {
      const dateA = a.startDate ? new Date(a.startDate).getTime() : Infinity;
      const dateB = b.startDate ? new Date(b.startDate).getTime() : Infinity;
      return dateA - dateB;
    });
  }, [phases]);

  // Build all possible columns (Einnahmen + Ausgaben per phase)
  const allColumns = useMemo(() => {
    const cols = [];

    sortedPhases.forEach((phase) => {
      const phaseKey = phase.name || phase.id;

      // Einnahmen column - always toggleable
      cols.push({
        key: `einnahmen|${phaseKey}`,
        type: "einnahmen",
        phase,
        phaseKey,
        partnerId: null,
        partnerName: null,
        label: phase.name || phase.id,
        shortLabel: phase.name || phase.id,
      });

      // Find unique partners for this phase from existing expense entries
      const phaseExpenses = projectEntries.filter(
        (e) =>
          e.typ === "Ausgabe" &&
          (e.phase === phase.name || e.phase === phase.id)
      );

      const partnerMap = {};
      phaseExpenses.forEach((e) => {
        const pid = e.partner_id || "other";
        if (!partnerMap[pid]) {
          partnerMap[pid] = e.partner_name || "Sonstige";
        }
      });

      // Merge in all project partners (so they show as columns when expanded)
      projectPartnersList.forEach((pp) => {
        const pid = String(pp.id);
        if (!partnerMap[pid]) {
          partnerMap[pid] = pp.name;
        }
      });

      // Merge in manually added partners for this phase
      const manualForPhase = manualPartnerColumns[phaseKey] || [];
      manualForPhase.forEach((mp) => {
        if (!partnerMap[mp.partnerId]) {
          partnerMap[mp.partnerId] = mp.partnerName;
        }
      });

      // Ausgaben columns per partner
      Object.entries(partnerMap)
        .sort(([, a], [, b]) => a.localeCompare(b))
        .forEach(([partnerId, partnerName]) => {
          cols.push({
            key: `ausgaben|${phaseKey}|${partnerId}`,
            type: "ausgaben",
            phase,
            phaseKey,
            partnerId,
            partnerName,
            label: `${partnerName}`,
            shortLabel: partnerName,
          });
        });

    });

    return cols;
  }, [sortedPhases, projectEntries, manualPartnerColumns, projectPartnersList]);

  // Unique planers (partners with expense entries) for the filter dropdown
  const uniquePlaners = useMemo(() => {
    const planerMap = {};
    projectEntries.forEach((e) => {
      if (e.typ === "Ausgabe" && e.partner_id && e.partner_id !== "other") {
        planerMap[e.partner_id] = e.partner_name || `Partner ${e.partner_id}`;
      }
    });
    // Also include project partners that may not have entries yet
    projectPartnersList.forEach((pp) => {
      const pid = String(pp.id);
      if (!planerMap[pid]) {
        planerMap[pid] = pp.name;
      }
    });
    return Object.entries(planerMap)
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [projectEntries, projectPartnersList]);

  // Visible columns = depends on whether a planer is selected
  const columns = useMemo(() => {
    if (selectedPlaner) {
      // Planer view: only show ausgaben columns for the selected planer,
      // with phase name as column header instead of partner name
      return allColumns
        .filter((col) => {
          if (col.type !== "ausgaben") return false;
          return String(col.partnerId) === String(selectedPlaner.id);
        })
        .map((col) => ({
          ...col,
          label: col.phaseKey,
          shortLabel: col.phaseKey,
        }));
    }
    // Default view: Einnahmen always, Ausgaben when phase is expanded
    return allColumns.filter((col) => {
      if (col.type === "einnahmen") return true;
      if (col.type === "ausgaben") return expandedPhases.has(col.phaseKey);
      return false;
    });
  }, [allColumns, expandedPhases, readOnly, selectedPlaner]);

  // Toggle phase expansion
  const handleTogglePhase = useCallback((phaseKey) => {
    setExpandedPhases((prev) => {
      const next = new Set(prev);
      if (next.has(phaseKey)) {
        next.delete(phaseKey);
      } else {
        next.add(phaseKey);
      }
      return next;
    });
  }, []);

  // Partner search suggestions
  const partnerSuggestions = useMemo(() => {
    if (!partnerSearchValue || partnerSearchValue.length < 2) return [];
    const q = partnerSearchValue.toLowerCase();
    // Project partners first, then others
    const projectMatches = projectPartnersList.filter((p) =>
      p.name?.toLowerCase().includes(q)
    );
    const otherMatches = allPartners.filter(
      (p) =>
        p.name?.toLowerCase().includes(q) &&
        !projectPartnersList.find((pp) => String(pp.id) === String(p.id))
    );
    return [...projectMatches, ...otherMatches].slice(0, 8);
  }, [partnerSearchValue, projectPartnersList, allPartners]);

  // Add a partner column for a phase (via local state so it appears immediately)
  const addPartnerColumnForPhase = useCallback((phaseKey, partnerId, partnerName) => {
    setManualPartnerColumns((prev) => {
      const existing = prev[phaseKey] || [];
      if (existing.find((p) => String(p.partnerId) === String(partnerId))) {
        return prev; // already exists
      }
      return { ...prev, [phaseKey]: [...existing, { partnerId: String(partnerId), partnerName }] };
    });
  }, []);

  // Select partner from suggestions
  const handleSelectPartner = useCallback(
    async (partner, phaseKey) => {
      const isLinked = projectPartnersList.find(
        (p) => String(p.id) === String(partner.id)
      );
      if (isLinked) {
        // Already linked to project - just add the column
        addPartnerColumnForPhase(phaseKey, partner.id, partner.name);
      } else {
        // Not linked to project - ask for role first
        setPendingPartner({ ...partner, phaseKey });
        setShowPartnerRoleModal(true);
      }
      setAddPartnerForPhase(null);
      setPartnerSearchValue("");
    },
    [projectPartnersList, addPartnerColumnForPhase]
  );

  // Confirm partner role and link to project
  const handlePartnerRoleConfirm = useCallback(
    async (role) => {
      if (!pendingPartner) return;
      try {
        await addProjectPartner(projectCode, pendingPartner.id, role);
        setProjectPartnersList((prev) => [
          ...prev,
          {
            id: pendingPartner.id,
            name: pendingPartner.name,
            role,
          },
        ]);
        // Add the column so it appears immediately
        addPartnerColumnForPhase(pendingPartner.phaseKey, pendingPartner.id, pendingPartner.name);
      } catch (err) {
        console.error("Error linking partner:", err);
      }
      setShowPartnerRoleModal(false);
      setPendingPartner(null);
    },
    [pendingPartner, projectCode, addPartnerColumnForPhase]
  );

  // Create new partner
  const handleCreateNewPartner = useCallback(
    async (phaseKey) => {
      if (!partnerSearchValue) return;
      try {
        const newPartner = await createPartner({
          name: partnerSearchValue,
          type: "Supplier",
        });
        setAllPartners((prev) => [...prev, newPartner]);
        setPendingPartner({ ...newPartner, phaseKey });
        setShowPartnerRoleModal(true);
      } catch (err) {
        console.error("Error creating partner:", err);
      }
      setAddPartnerForPhase(null);
      setPartnerSearchValue("");
    },
    [partnerSearchValue]
  );

  // Save cell (create or update entry)
  const handleSaveCell = useCallback(
    async (column, week, amount, existingEntry) => {
      if (readOnly || saving) return;
      setSaving(true);

      try {
        // Individuelle Felder bestehender Einträge erhalten: Beim Betrags-Edit
        // wurden sonst Name, Datum, Zahlungsfrist und MwSt-Satz mit den
        // globalen/berechneten Werten überschrieben (Fix 2026-07-24).
        const mwstSatz = existingEntry?.mwst_satz ?? globalSettings.mwstSatz;
        const mwst = calculateMwstAmounts(amount, mwstSatz, false);
        const invoiceDate = getInvoiceDateForWeek(
          week.number,
          week.year,
          globalSettings.rechnungstag
        );
        const phaseKey = column.phase?.name || column.phase?.id || "";

        // Datum nur neu berechnen, wenn der Eintrag die Woche wechselt
        // (Drag-Move); beim Edit in derselben Woche bleibt das Datum erhalten.
        const existingWeek = existingEntry
          ? getEffectiveWeekForDate(existingEntry.wann || existingEntry.versand_datum)
          : null;
        const staysInWeek =
          existingWeek &&
          existingWeek.weekNum === week.number &&
          existingWeek.year === week.year;
        const newDateISO = formatDateISO(invoiceDate);
        const wann = staysInWeek ? existingEntry.wann || newDateISO : newDateISO;
        const versandDatum = staysInWeek
          ? existingEntry.versand_datum || wann
          : newDateISO;

        // Generate invoice reference for new entries
        const invoiceRef =
          existingEntry?.invoice_reference ||
          generateInvoiceReference(
            globalSettings.rechnungsnummerPrefix,
            phaseKey,
            projectEntries.filter(
              (e) =>
                (e.phase === column.phase?.name || e.phase === column.phase?.id) &&
                e.typ === (column.type === "einnahmen" ? "Einnahme" : "Ausgabe")
            )
          );

        const entryData = {
          id: existingEntry?.id || undefined,
          typ: column.type === "einnahmen" ? "Einnahme" : "Ausgabe",
          typ_timing: "Einmalig",
          name:
            existingEntry?.name ||
            (column.type === "einnahmen"
              ? `${projectCode} - ${phaseKey}`
              : `${projectCode} - ${column.partnerName || phaseKey}`),
          konto: existingEntry?.konto || (column.type === "einnahmen" ? "3200" : "4400"),
          projekt: projectCode,
          phase: column.phaseKey || column.phase?.name || column.phase?.id || "",
          partner_id: column.partnerId || "",
          partner_name: column.partnerName || "",
          versand_datum: versandDatum,
          wann, // backward compat
          betrag: mwst.nettoBetrag,
          netto_betrag: mwst.nettoBetrag,
          brutto_betrag: mwst.bruttoBetrag,
          mwst_satz: mwstSatz,
          mwst_betrag: mwst.mwstBetrag,
          ist_brutto: false,
          zahlungsfrist: existingEntry?.zahlungsfrist || globalSettings.zahlungsfrist,
          invoice_reference: invoiceRef,
          source: existingEntry?.source || "zahlungsplan",
          status:
            existingEntry?.status ||
            (column.type === "einnahmen" ? "expected" : "expecting"),
          // Preserve existing fields
          sent_date: existingEntry?.sent_date || "",
          paid_date: existingEntry?.paid_date || "",
          beleg_id: existingEntry?.beleg_id || "",
        };

        await saveBudgetEntry(entryData);
        await onBudgetChange();
      } catch (err) {
        console.error("Error saving Zahlungsplan entry:", err);
      } finally {
        setSaving(false);
      }
    },
    [
      readOnly,
      saving,
      globalSettings,
      projectCode,
      projectEntries,
      onBudgetChange,
    ]
  );

  // Double-click: open detail popover
  const handleDoubleClickCell = useCallback((column, week, entry) => {
    // Get cell element bounding rect from event
    const target = document.querySelector(
      `.zp-row.current-week .zp-cell, .zp-cell.has-entry`
    );
    // Use a simpler approach: fixed position near center
    setDetailPopover({
      entry,
      anchorRect: {
        top: window.innerHeight / 2 - 200,
        left: window.innerWidth / 2 - 160,
        right: window.innerWidth / 2 + 160,
        bottom: window.innerHeight / 2 + 200,
      },
    });
  }, []);

  // Context menu (right-click status change)
  const handleContextMenu = useCallback((e, entry) => {
    setContextMenu({
      entry,
      position: { x: e.clientX, y: e.clientY },
    });
  }, []);

  // Status change
  const handleStatusChange = useCallback(
    async (entry, newStatus) => {
      try {
        const additionalData = {};
        if (newStatus === "sent") {
          additionalData.sent_date = formatDateISO(new Date());
        }
        if (newStatus === "paid") {
          additionalData.paid_date = formatDateISO(new Date());
        }
        await updateBudgetEntryStatus(entry.id, newStatus, additionalData);
        await onBudgetChange();
        // Ausgabe neu auf "Erhalten": fragen, ob ein Beleg zugeordnet werden soll
        if (
          !readOnly &&
          newStatus === "received" &&
          entry.typ === "Ausgabe" &&
          entry.status !== "received"
        ) {
          setBelegDialog({
            entry: { ...entry, status: "received" },
            statusChanged: true,
          });
        }
      } catch (err) {
        console.error("Error updating status:", err);
      }
    },
    [onBudgetChange, readOnly]
  );

  // Beleg per Kontextmenü zuordnen (auch bei bereits erhaltenen/bezahlten
  // Ausgaben) — ohne Statuswechsel
  const handleAssignBeleg = useCallback((entry) => {
    setBelegDialog({ entry, statusChanged: false });
  }, []);

  // Betrag bearbeiten per Kontextmenü: die betroffene Zelle geht in den
  // Editiermodus (Direktklick auf gefüllte Zellen ist bewusst deaktiviert)
  const [editRequestId, setEditRequestId] = useState(null);
  const handleEditRequest = useCallback((entry) => {
    setEditRequestId(entry.id);
  }, []);
  const handleEditRequestHandled = useCallback(() => {
    setEditRequestId(null);
  }, []);

  // Beleg hochladen (und lokale Liste aktualisieren)
  const handleBelegUpload = useCallback(
    async (file) => {
      const beleg = await uploadBeleg(
        projectCode,
        file,
        user?.email || user?.userDetails || ""
      );
      setBelege((prev) => [beleg, ...prev]);
      return beleg;
    },
    [projectCode, user]
  );

  // Positionen einem Beleg zuordnen; noch offene Positionen werden dabei
  // auf "Erhalten" gesetzt (weiter fortgeschrittene Status bleiben unverändert).
  const handleBelegLink = useCallback(
    async (beleg, entriesToLink) => {
      for (const e of entriesToLink) {
        const nextStatus =
          !e.status || e.status === "expecting" || e.status === "overdue"
            ? "received"
            : e.status;
        await updateBudgetEntryStatus(e.id, nextStatus, { beleg_id: beleg.id });
      }
      await onBudgetChange();
    },
    [onBudgetChange]
  );

  // Beleg löschen: Verknüpfungen auf den Zahlungen lösen, dann Blob + Zeile weg
  const handleBelegDelete = useCallback(
    async (beleg) => {
      try {
        const linked = projectEntries.filter((e) => e.beleg_id === beleg.id);
        for (const e of linked) {
          await updateBudgetEntryStatus(e.id, e.status, { beleg_id: "" });
        }
        await deleteBeleg(beleg);
        setBelege((prev) => prev.filter((b) => b.id !== beleg.id));
        if (linked.length > 0) {
          await onBudgetChange();
        }
      } catch (err) {
        console.error("Error deleting Beleg:", err);
      }
    },
    [projectEntries, onBudgetChange]
  );

  // Delete entry from detail popover
  const handleDeleteEntry = useCallback(
    async (entry) => {
      try {
        await deleteBudgetEntry(entry.id);
        await onBudgetChange();
      } catch (err) {
        console.error("Error deleting entry:", err);
      }
    },
    [onBudgetChange]
  );

  // Save entry from detail popover
  const handleDetailSave = useCallback(
    async (entry, updates) => {
      try {
        const mwst = calculateMwstAmounts(updates.netto_betrag, globalSettings.mwstSatz, false);
        await saveBudgetEntry({
          ...entry,
          netto_betrag: mwst.nettoBetrag,
          betrag: mwst.nettoBetrag,
          brutto_betrag: mwst.bruttoBetrag,
          mwst_betrag: mwst.mwstBetrag,
          mwst_satz: globalSettings.mwstSatz,
          invoice_reference: updates.invoice_reference,
          status: updates.status,
          wann: updates.wann,
          zahlungsfrist: updates.zahlungsfrist,
          name: updates.name,
        });
        await onBudgetChange();
        // Auch hier: Ausgabe neu auf "Erhalten" gestellt → Beleg-Dialog anbieten
        if (
          !readOnly &&
          updates.status === "received" &&
          entry.typ === "Ausgabe" &&
          entry.status !== "received"
        ) {
          setBelegDialog({
            entry: { ...entry, status: "received" },
            statusChanged: true,
          });
        }
      } catch (err) {
        console.error("Error saving entry details:", err);
      }
    },
    [globalSettings.mwstSatz, onBudgetChange, readOnly]
  );

  return (
    <div className="zp-modal-overlay" onClick={onClose}>
      <div
        className={`zp-modal${isFullscreen ? " zp-modal--fullscreen" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="zp-modal-header">
          <h2>Zahlungsplan – {projectCode} {project?.title ? `– ${project.title}` : ""}</h2>
          <div className="zp-modal-header-actions">
            <button
              className="btn btn-secondary"
              onClick={() => setShowBelegeListe(true)}
              title="Alle Belege dieses Projekts anzeigen"
            >
              Belege{belege.length > 0 ? ` (${belege.length})` : ""}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setIsFullscreen((v) => !v)}
              title={isFullscreen ? "Vollbild beenden (Esc)" : "Vollbild"}
            >
              {isFullscreen ? "Vollbild beenden" : "Vollbild"}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() =>
                exportZahlungsplanToExcel({
                  projectCode,
                  projectTitle: project?.title,
                  phases: sortedPhases,
                  entries: projectEntries,
                  columns,
                  globalSettings,
                  hideEmptyWeeks,
                  selectedPlaner,
                  incomeBudgets,
                })
              }
              title="Als Excel exportieren"
            >
              Excel Export
            </button>
            <button className="btn btn-secondary" onClick={onClose}>
              Schliessen
            </button>
          </div>
        </div>

        {/* Global Settings */}
        <ZahlungsplanHeader
          settings={globalSettings}
          onChange={setGlobalSettings}
          readOnly={readOnly}
          hideEmptyWeeks={hideEmptyWeeks}
          onToggleHideEmpty={() => setHideEmptyWeeks((v) => !v)}
          uniquePlaners={uniquePlaners}
          selectedPlaner={selectedPlaner}
          onSelectPlaner={setSelectedPlaner}
        />

        {/* Table */}
        <div className="zp-modal-body">
          {phases.length === 0 ? (
            <div className="zp-empty-state">
              Keine Phasen vorhanden. Erstellen Sie zuerst Phasen im Controlling.
            </div>
          ) : (
            <ZahlungsplanTable
              phases={sortedPhases}
              entries={projectEntries}
              columns={columns}
              globalSettings={globalSettings}
              readOnly={readOnly}
              expandedPhases={expandedPhases}
              onTogglePhase={handleTogglePhase}
              onSaveCell={handleSaveCell}
              onDeleteEntry={handleDeleteEntry}
              onDoubleClickCell={handleDoubleClickCell}
              onContextMenu={handleContextMenu}
              addPartnerForPhase={addPartnerForPhase}
              onOpenPartnerSearch={setAddPartnerForPhase}
              partnerSearchValue={partnerSearchValue}
              onPartnerSearchChange={setPartnerSearchValue}
              partnerSuggestions={partnerSuggestions}
              onSelectPartner={handleSelectPartner}
              onCreateNewPartner={handleCreateNewPartner}
              hideEmptyWeeks={hideEmptyWeeks}
              selectedPlaner={selectedPlaner}
              incomeBudgets={incomeBudgets}
              onIncomeBudgetChange={(phaseKey, value) =>
                setIncomeBudgets((prev) => ({ ...prev, [phaseKey]: value }))
              }
              editRequestId={editRequestId}
              onEditRequestHandled={handleEditRequestHandled}
            />
          )}
        </div>

        {/* Context Menu */}
        {contextMenu && (
          <ZahlungsplanContextMenu
            entry={contextMenu.entry}
            position={contextMenu.position}
            onStatusChange={handleStatusChange}
            onAssignBeleg={handleAssignBeleg}
            onEdit={handleEditRequest}
            onClose={() => setContextMenu(null)}
          />
        )}

        {/* Detail Popover */}
        {detailPopover && (
          <ZahlungsplanDetailPopover
            entry={detailPopover.entry}
            anchorRect={detailPopover.anchorRect}
            globalSettings={globalSettings}
            onSave={handleDetailSave}
            onDelete={handleDeleteEntry}
            onClose={() => setDetailPopover(null)}
            readOnly={readOnly}
          />
        )}

        {/* Beleg-Dialog (Statuswechsel auf "Erhalten" oder Kontextmenü) */}
        {belegDialog && (
          <ZahlungsplanBelegDialog
            entry={belegDialog.entry}
            entries={projectEntries}
            belege={belege}
            phases={sortedPhases}
            statusChanged={belegDialog.statusChanged}
            onUpload={handleBelegUpload}
            onLink={handleBelegLink}
            onClose={() => setBelegDialog(null)}
          />
        )}

        {/* Belege-Übersicht */}
        {showBelegeListe && (
          <ZahlungsplanBelegeListe
            belege={belege}
            entries={projectEntries}
            phases={sortedPhases}
            readOnly={readOnly}
            onDelete={handleBelegDelete}
            onClose={() => setShowBelegeListe(false)}
          />
        )}

        {/* Partner Role Modal */}
        {showPartnerRoleModal && pendingPartner && (
          <div className="zp-role-modal-overlay" onClick={() => { setShowPartnerRoleModal(false); setPendingPartner(null); }}>
            <div className="zp-role-modal" onClick={(e) => e.stopPropagation()}>
              <h4>Partner verknüpfen</h4>
              <p className="zp-role-modal-info">
                <strong>{pendingPartner.name}</strong> ist noch nicht mit diesem Projekt verknüpft.
                Wählen Sie eine Rolle:
              </p>
              <div className="zp-role-options">
                {[
                  { value: "partner", label: "Partner" },
                  { value: "consultant", label: "Berater" },
                  { value: "contractor", label: "Unternehmer" },
                  { value: "auftraggeberin", label: "Auftraggeberin" },
                ].map((role) => (
                  <button
                    key={role.value}
                    className="zp-role-option"
                    onClick={() => handlePartnerRoleConfirm(role.value)}
                  >
                    {role.label}
                  </button>
                ))}
              </div>
              <button
                className="zp-role-cancel"
                onClick={() => { setShowPartnerRoleModal(false); setPendingPartner(null); }}
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ZahlungsplanModal;
